import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { AuthBrandMark } from "@/components/auth/AuthBrandMark";
import { useAuth } from "@/contexts/AuthContext";
import {
  sendMyVerificationEmail,
  verifyMyEmail,
} from "@/services/authService";

export function VerifyEmailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") ?? "/";
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSendCode = async (): Promise<void> => {
    setSending(true);
    try {
      await sendMyVerificationEmail(i18n.resolvedLanguage ?? i18n.language);
      toast.success(t("auth.verifyEmail.codeSent"));
    } catch {
      toast.error(t("auth.verifyEmail.sendError"));
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (): Promise<void> => {
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error(t("auth.verifyEmail.codeRequired"));
      return;
    }
    setVerifying(true);
    try {
      await verifyMyEmail(trimmed);
      await refreshUser();
      toast.success(t("auth.verifyEmail.success"));
      const path = returnTo.startsWith("/") ? returnTo : `/${returnTo}`;
      navigate(path !== "/verify-email" ? path : "/", { replace: true });
    } catch {
      toast.error(t("auth.verifyEmail.verifyError"));
    } finally {
      setVerifying(false);
    }
  };

  const resolvedReturnTo = returnTo.startsWith("/") ? returnTo : `/${returnTo}`;
  if (user?.emailVerifiedAt && resolvedReturnTo !== "/verify-email") {
    return <Navigate to={resolvedReturnTo} replace />;
  }

  return (
    <div className="fx-auth-stage fx-auth-status">
      <div className="fx-auth-status-inner">
        <AuthBrandMark />
        <h1 className="fx-auth-status-title">
          {t("auth.verifyEmail.titleLead", "Verificá tu")}{" "}
          <span className="ital">
            {t("auth.verifyEmail.titleAccent", "correo.")}
          </span>
        </h1>
        <p className="fx-auth-status-message">
          {t("auth.verifyEmail.subtitle", {
            email: user?.email ?? "",
            defaultValue: "Te enviamos un código a tu correo. Ingresalo abajo.",
          })}
        </p>

        <form
          className="fx-auth-form"
          style={{ width: "100%", maxWidth: 360 }}
          onSubmit={(e) => {
            e.preventDefault();
            void handleVerify();
          }}
        >
          <div className="fx-auth-field">
            <label htmlFor="code">{t("auth.verifyEmail.codeLabel")}</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              style={{
                fontFamily: "var(--fx-ff-mono)",
                textAlign: "center",
                letterSpacing: "0.3em",
                fontSize: 18,
              }}
            />
          </div>

          <button
            type="submit"
            className="fx-auth-btn fx-auth-btn-primary"
            disabled={verifying || code.trim().length < 4}
          >
            {verifying ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              t("auth.verifyEmail.verifyButton")
            )}
          </button>

          <button
            type="button"
            className="fx-auth-btn fx-auth-btn-ghost"
            onClick={() => void handleSendCode()}
            disabled={sending}
          >
            {sending ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              t("auth.verifyEmail.sendCode")
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
